/**
 * Production report. The plant's day from washing to dispatch, and the
 * hours, manpower, power and material behind it, summed over any period the
 * person chooses — a day, a week, a month, a custom range — and set against
 * the period it is compared with.
 *
 * Every card and every row opens what it is made of, down to the ledger
 * lines of each entry and the rolls off each loom.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../lib/api";
import {
  controls,
  type ProductionReport as Report,
  type ReportCard,
} from "../../lib/controls";
import { fmtDate, fmtPct, fmtQty, toNumber } from "../../lib/format";
import { comparisonLabel, describePeriod } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";
import { PeriodPicker } from "../../components/PeriodPicker";
import { DrillCard, useDrill } from "../../components/Drill";
import { Info } from "../../components/Info";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

const n = (v: string | number | null | undefined) => toNumber(v) || 0;

type MachineRow = Report["machines"][number];
type DayRow = Report["days"][number];
type ReasonRow = Report["reasons"][number];
type PlantRow = Report["plant_days"][number];

const qty = (key: keyof MachineRow & string, label: string, dp = 2):
  SheetColumn<MachineRow> => ({
  key, label, type: "number", numeric: true,
  value: (r) => n(r[key] as string), format: (r) => fmtQty(r[key] as string, dp),
});

const MACHINE_COLUMNS: SheetColumn<MachineRow>[] = [
  { key: "machine", label: "Machine", value: (r) => r.machine },
  { key: "runs", label: "Entries", type: "number", numeric: true, value: (r) => r.runs,
    info: "Shift entries for the machine in the period." },
  { ...qty("hours", "Hours", 1), info: "Running hours on its shift entries, summed." },
  { key: "manpower", label: "Manpower", type: "number", numeric: true, value: (r) => r.manpower,
    info: "Persons on its shift entries, summed — person-shifts." },
  { ...qty("kwh", "kWh", 0), info: "Power units entered on its shift entries, summed." },
  { ...qty("input", "Input kg"), info: "Material issued into the machine, kg." },
  { ...qty("output", "Output kg"), info: "Material the machine produced, kg." },
  { ...qty("loss", "Waste kg"), info: "Waste the machine declared, kg." },
  { ...qty("variance", "Unaccounted kg"), info: "Input − output − waste, kg." },
  { key: "variance_pct", label: "Unaccounted %", type: "number", numeric: true,
    value: (r) => n(r.variance_pct), format: (r) => fmtPct(r.variance_pct),
    info: "Unaccounted kg ÷ input kg." },
  { key: "tolerance_pct", label: "Tolerance", type: "number", numeric: true,
    value: (r) => n(r.tolerance_pct), format: (r) => fmtPct(r.tolerance_pct),
    info: "The unaccounted % a shift may carry before it needs a reason, set in Settings → Machines." },
  { key: "out_of_tolerance", label: "Outside", type: "number", numeric: true,
    value: (r) => r.out_of_tolerance,
    info: "Shift entries whose unaccounted % is beyond the tolerance." },
];

const DAY_COLUMNS: SheetColumn<DayRow>[] = [
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "runs", label: "Entries", type: "number", numeric: true, value: (r) => r.runs,
    info: "Shift entries on the day." },
  { key: "hours", label: "Hours", type: "number", numeric: true, value: (r) => n(r.hours),
    format: (r) => fmtQty(r.hours, 1), info: "Running hours on the day's shift entries, summed." },
  { key: "manpower", label: "Manpower", type: "number", numeric: true, value: (r) => r.manpower,
    info: "Persons on the day's shift entries, summed." },
  { key: "kwh", label: "kWh", type: "number", numeric: true, value: (r) => n(r.kwh),
    format: (r) => fmtQty(r.kwh, 0), info: "Power units on the day's shift entries, summed." },
  { key: "variance", label: "Unaccounted kg", type: "number", numeric: true,
    value: (r) => n(r.variance), format: (r) => fmtQty(r.variance, 2),
    info: "Input − output − waste on the day's shift entries, kg." },
];

const REASON_COLUMNS: SheetColumn<ReasonRow>[] = [
  { key: "reason", label: "Reason", value: (r) => r.reason },
  { key: "runs", label: "Entries", type: "number", numeric: true, value: (r) => r.runs,
    info: "Shift entries that gave this reason." },
  { key: "variance", label: "Unaccounted kg", type: "number", numeric: true,
    value: (r) => n(r.variance), format: (r) => fmtQty(r.variance, 2),
    info: "Unaccounted kg on those entries." },
  { key: "variance_pct", label: "Of input", type: "number", numeric: true,
    value: (r) => n(r.variance_pct), format: (r) => fmtPct(r.variance_pct),
    info: "Unaccounted kg ÷ input kg on those entries." },
  { key: "machines", label: "Machines", value: (r) => r.machines.join(", ") },
];

/** The plant summary's columns, in the order the daily MIS reads. `info`
 *  is the server's working for each (PLANT_INFO in services/reports.py), so
 *  the column and the card say the same thing. */
function plantColumns(info: Record<string, string>): SheetColumn<PlantRow>[] {
  const num = (key: keyof PlantRow & string, label: string, dp = 0): SheetColumn<PlantRow> => ({
    key, label, type: "number", numeric: true, info: info[key],
    value: (r) => (r[key] === null ? null : n(r[key] as string)),
    format: (r) => (r[key] === null ? "—" : fmtQty(r[key] as string, dp)),
  });
  const pct = (key: keyof PlantRow & string, label: string): SheetColumn<PlantRow> => ({
    key, label, type: "number", numeric: true, info: info[key],
    value: (r) => n(r[key] as string), format: (r) => fmtPct(r[key] as string | null),
  });
  return [
    { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
      format: (r) => fmtDate(r.business_date) },
    num("washing_kg", "Washing kg"),
    num("rp_kg", "RP kg"),
    num("agglo_kg", "Agloo kg"),
    num("tape_kg", "Tape kg"),
    num("tape_waste_kg", "Tape wastage kg"),
    pct("tape_waste_pct", "Tape wastage %"),
    num("loom_mtr", "Loom m"),
    num("loom_kg", "Loom kg"),
    pct("loom_pct", "Loom %"),
    num("loom_waste_kg", "Loom wastage kg"),
    pct("loom_waste_pct", "Loom wastage %"),
    num("stitching_pcs", "Stitching pcs"),
    num("printing_pcs", "Printing pcs"),
    num("dispatch_pcs", "Dispatch pcs"),
    num("dispatch_kg", "Dispatch kg"),
    { key: "manpower", label: "Manpower", type: "number", numeric: true, info: info.manpower,
      value: (r) => r.manpower, format: (r) => fmtQty(r.manpower, 0) },
    num("kwh", "Electricity kWh"),
  ];
}


/** The change against the comparison period, with an arrow as well as a
 *  colour so meaning never rides on hue alone. */
function Delta({ card, vs }: { card: ReportCard; vs?: string }) {
  if (!vs || card.previous === undefined) return null;
  const now = toNumber(card.value);
  const before = toNumber(card.previous);
  if (card.value === null || card.previous === null || !before) {
    return <span className="kpi__delta kpi__delta--neutral">nothing to compare {vs}</span>;
  }
  const change = ((now - before) / Math.abs(before)) * 100;
  const up = change > 0;
  const flat = Math.abs(change) < 0.05;
  const good = card.inverted ? !up : up;
  const tone = flat ? "neutral" : good ? "positive" : "negative";
  return (
    <span className={`kpi__delta kpi__delta--${tone}`}
          title={`${fmtQty(card.previous, 0)}${card.unit ? ` ${card.unit}` : ""} ${vs}`}>
      {flat ? "→" : up ? "↑" : "↓"} {fmtPct(Math.abs(change).toFixed(1))} {vs}
    </span>
  );
}

export function Cards({ cards, vs }: { cards: ReportCard[]; vs?: string }) {
  return (
    <div className="kpirow">
      {cards.map((c) => (
        <DrillCard key={c.key} drill={c.drill}>
          <span className="label">{c.label}<Info text={c.info} /></span>
          <span className={`kpi-value num${c.tone === "warn" ? " kpi-value--warn" : ""}`}>
            {c.value === null
              ? "—"
              : fmtQty(c.value, c.unit === "kg" || c.unit === "m" ? 0 : Number.isInteger(Number(c.value)) ? 0 : 1)}
            {c.unit && c.value !== null && <span className="kpi__unit"> {c.unit}</span>}
          </span>
          <Delta card={c} vs={vs} />
          {c.note && <span className="kpi__sub">{c.note}</span>}
        </DrillCard>
      ))}
    </div>
  );
}

type Tab = "plant" | "machines" | "days" | "reasons";

export function ProductionReport() {
  const [period, setPeriod] = useReportPeriod();
  const [compare, setCompare] = useCompare();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("plant");
  const open = useDrill();

  const load = useCallback(() => {
    setReport(null);
    setError(null);
    controls
      .production(period.from, period.to, [], period.shift, compare)
      .then(setReport)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const plantCols = useMemo(() => plantColumns(report?.plant_info ?? {}), [report]);
  const plant = useSheet(report?.plant_days ?? [], plantCols);
  const machines = useSheet(report?.machines ?? [], MACHINE_COLUMNS);
  const days = useSheet(report?.days ?? [], DAY_COLUMNS);
  const reasons = useSheet(report?.reasons ?? [], REASON_COLUMNS);

  const tables = useMemo(
    () => ({
      plant: { sheet: plant, name: "plant-summary-by-day" },
      machines: { sheet: machines, name: "production-by-machine" },
      days: { sheet: days, name: "production-by-day" },
      reasons: { sheet: reasons, name: "unaccounted-reasons" },
    }),
    [plant, machines, days, reasons]
  );
  const current = tables[tab];
  const vs = comparisonLabel(period, compare);

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Production report</h2>
          <p className="page__sub">
            {describePeriod(period)} · {vs} · press any card or row to see what it is made of
          </p>
        </div>
        <div className="page__controls">
          <PeriodPicker value={period} onChange={setPeriod} shifts
                        compare={compare} onCompare={setCompare} />
          <ReportDownload title="Production report" period={period} />
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}
      {!report && !error && (
        <div className="kpirow">
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton kpi" style={{ height: 108 }} />)}
        </div>
      )}

      {report && (
        <>
          <h3 className="section-title">Plant summary</h3>
          <p className="section-note">Washing to dispatch, with manpower and electricity</p>
          <Cards cards={report.plant_cards} vs={vs} />

          <h3 className="section-title">Shift entries</h3>
          <Cards cards={report.cards} vs={vs} />

          <h3 className="section-title">Loom floor</h3>
          <p className="section-note">
            {report.loom.loom_shifts} loom shifts in the period
          </p>
          <Cards cards={report.loom_cards} vs={vs} />

          <div className="tabs" role="tablist">
            {([
              ["plant", "Plant summary"],
              ["machines", "By machine"],
              ["days", "By day"],
              ["reasons", "Unaccounted reasons"],
            ] as const).map(([key, label]) => (
              <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>

          <SheetToolbar sheet={current.sheet as never} exportName={current.name}>
            <span className="page__note">
              {tab === "reasons"
                ? "Every reason given for unaccounted material, and entries outside tolerance with none."
                : tab === "plant"
                  ? "One row a day. Hover the (i) on a heading for how it is worked out."
                  : "Press a row to break it down."}
            </span>
          </SheetToolbar>
          <div className="tablewrap">
            <table className="table sheet">
              {tab === "plant" && (
                <>
                  <SheetHead sheet={plant} />
                  <tbody>
                    {plant.rows.map((r) => (
                      <tr key={r.business_date} className="rowlink" tabIndex={0}
                          onClick={() => open(r.drill)}
                          onKeyDown={(e) => e.key === "Enter" && open(r.drill)}>
                        {plantCols.map((c) => (
                          <td key={c.key} className={c.numeric ? "num mono" : undefined}>
                            {c.format ? c.format(r) : String(c.value(r) ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {plant.rows.length === 0 && (
                      <tr><td colSpan={plantCols.length}>Nothing produced in this period.</td></tr>
                    )}
                  </tbody>
                  {plant.rows.length > 0 && (
                    <tfoot>
                      <tr className="table__total">
                        {plantCols.map((c) => (
                          <td key={c.key} className={c.numeric ? "num mono" : undefined}>
                            {c.key === "date"
                              ? "Total"
                              : c.format!({ ...report.plant_total, business_date: "", drill: plant.rows[0].drill })}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </>
              )}
              {tab === "machines" && (
                <>
                  <SheetHead sheet={machines} />
                  <tbody>
                    {machines.rows.map((r) => (
                      <tr key={r.machine_code} className="rowlink" tabIndex={0}
                          onClick={() => open(r.drill)}
                          onKeyDown={(e) => e.key === "Enter" && open(r.drill)}>
                        {MACHINE_COLUMNS.map((c) => (
                          <td key={c.key} className={c.numeric ? "num mono" : undefined}>
                            {c.format ? c.format(r) : String(c.value(r) ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
              {tab === "days" && (
                <>
                  <SheetHead sheet={days} />
                  <tbody>
                    {days.rows.map((r) => (
                      <tr key={r.business_date} className="rowlink" tabIndex={0}
                          onClick={() => open(r.drill)}
                          onKeyDown={(e) => e.key === "Enter" && open(r.drill)}>
                        {DAY_COLUMNS.map((c) => (
                          <td key={c.key} className={c.numeric ? "num mono" : undefined}>
                            {c.format ? c.format(r) : String(c.value(r) ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
              {tab === "reasons" && (
                <>
                  <SheetHead sheet={reasons} />
                  <tbody>
                    {reasons.rows.map((r) => (
                      <tr key={r.reason} className="rowlink" tabIndex={0}
                          onClick={() => open(r.drill)}
                          onKeyDown={(e) => e.key === "Enter" && open(r.drill)}>
                        {REASON_COLUMNS.map((c) => (
                          <td key={c.key} className={c.numeric ? "num mono" : undefined}>
                            {c.format ? c.format(r) : String(c.value(r) ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {reasons.rows.length === 0 && (
                      <tr><td colSpan={REASON_COLUMNS.length}>No unaccounted material needed a reason in this period.</td></tr>
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}
